import { WritableSignal } from '@angular/core';
import { catchError, of, take } from 'rxjs';

import {
  EntityInterface,
  EntitySummaryType,
  TableColCfg
} from '@gfe/core/models/entities/entity-visualization-interface';
import { PagedList } from '@gfe/core/models/paged-list';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { isNil } from '@gfe/core/utils/shared-utils';

import { EntitySelectorRequestModel } from '@core/models/entity/entity-selector-request.model';

import { SummaryItem } from './summary-item';

export type SummaryStatus = 'idle' | 'loading' | 'ready' | 'exceeded';

export const PASSIVE_SUMMARY_MAX_ROWS = 200;
export const SELECT_ALL_SUMMARY_MAX_ROWS = 500;

export interface SummaryFirstPage<T> {
  rows: EntityInterface<T>[];
  page: number;
  pageSize: number;
  total: number;
}

export class EntitySummary<T> {
  static SUMMARY_TYPES: EntitySummaryType[] = [
    'count',
    'sum',
    'avg',
    'min',
    'max',
    'distinct'
  ];

  private currentRequest: EntitySelectorRequestModel | null = null;

  private summaryItems: SummaryItem<T>[] = [];

  private runId = 0;
  private hasColumnsToSummary = false;

  private readonly columns: TableColCfg<EntityInterface<T>>[];
  private summaryItemsSignal: WritableSignal<SummaryItem<T>[]>;
  private selectorsService?: SelectorsService;

  private sumaryMode: 'lazy' | 'static' = 'lazy';
  isLazyDataLoadaded = false;
  private statusSignal?: WritableSignal<SummaryStatus>;
  private maxRows: number = Infinity;
  private seed: SummaryFirstPage<T> | null = null;

  private constructor(
    columns: TableColCfg<EntityInterface<T>>[],
    summaryItemsSignal: WritableSignal<SummaryItem<T>[]>,
    selectorsService?: SelectorsService
  ) {
    this.columns = columns;
    this.summaryItemsSignal = summaryItemsSignal;
    this.selectorsService = selectorsService;

    this.hasColumnsToSummary = columns.some((col) => (col.summaryTypes ?? []).length > 0);
  }

  static createLazySummary<T>(
    columns: TableColCfg<EntityInterface<T>>[],
    summaryItemsSignal: WritableSignal<SummaryItem<T>[]>,
    selectorsService: SelectorsService,
    statusSignal?: WritableSignal<SummaryStatus>
  ): EntitySummary<T> {
    const summary = new EntitySummary<T>(columns, summaryItemsSignal, selectorsService);
    summary.sumaryMode = 'lazy';
    summary.statusSignal = statusSignal;
    return summary;
  }

  static createStaticSummary<T>(
    columns: TableColCfg<EntityInterface<T>>[],
    summaryItemsSignal: WritableSignal<SummaryItem<T>[]>
  ): EntitySummary<T> {
    const summary = new EntitySummary<T>(columns, summaryItemsSignal);
    summary.sumaryMode = 'static';
    return summary;
  }

  getSummaryItems(): SummaryItem<T>[] {
    return this.summaryItems;
  }

  updateRequest(
    request: EntitySelectorRequestModel,
    maxRows: number = Infinity,
    firstPage?: SummaryFirstPage<T>
  ): void {
    if (this.sumaryMode === 'static') {
      console.warn('Static mode does not support request changes');
      return;
    }
    if (
      this.hasColumnsToSummary &&
      (isNil(this.currentRequest) ||
        !this.isSameRequest(request) ||
        this.maxRows !== maxRows)
    ) {
      this.maxRows = maxRows;
      this.runId++;
      this.currentRequest = request;
      this.seed = this.isUsableSeed(firstPage) ? firstPage : null;
      this.resetSummaryItems();
      this.startSummary();
      return;
    }
  }

  cancel(): void {
    if (this.sumaryMode === 'static') {
      console.warn('Static mode does not support cancellation');
      return;
    }
    // Bump the run id to make any in-flight walk abort cleanly on its next checkpoint.
    this.runId++;
    this.currentRequest = null;
    this.seed = null;
    this.isLazyDataLoadaded = false;
    this.summaryItems = [];
    this.summaryItemsSignal?.set([]);
    this.statusSignal?.set('idle');
  }

  updateStaticData(entities: EntityInterface<T>[]): void {
    if (this.sumaryMode === 'lazy') {
      console.warn('Lazy mode does not support static data changes');
      return;
    }
    if (this.hasColumnsToSummary) {
      this.resetSummaryItems();
      this.summaryItems.forEach((summaryItem) => {
        summaryItem.addValues(entities);
      });
      this.summaryItemsSignal?.set(this.summaryItems);
    }
  }

  private resetSummaryItems(): void {
    this.summaryItems = [];
    this.columns
      .filter((col) => (col.summaryTypes ?? []).length > 0)
      .forEach((column) => {
        this.summaryItems.push(new SummaryItem(column));
      });
  }

  private isUsableSeed(firstPage?: SummaryFirstPage<T>): firstPage is SummaryFirstPage<T> {
    return !isNil(firstPage) && firstPage.page === 1 && firstPage.pageSize > 0;
  }

  private isSameRequest(request: EntitySelectorRequestModel): boolean {
    return JSON.stringify(request) === JSON.stringify(this.currentRequest);
  }

  private startSummary(): void {
    const seed = this.seed;
    const pageSize = seed?.pageSize ?? 100;
    const lastPage = isNil(seed) ? Infinity : Math.ceil(seed.total / pageSize);
    let page = isNil(seed) ? 1 : 2;
    (async () => {
      this.isLazyDataLoadaded = false;
      this.statusSignal?.set('loading');
      let shouldNotify = true;
      const startedRunId = this.runId;

      if (!isNil(seed)) {
        if (seed.total > this.maxRows) {
          this.markExceeded();
          return;
        }
        this.summaryItems.forEach((summaryItem) => {
          summaryItem.addValues(seed.rows);
        });
      }

      while (page <= lastPage) {
        if (this.runId !== startedRunId) {
          this.resetSummaryItems();
          shouldNotify = false;
          break;
        }
        const data = await this.getData(page, pageSize);
        if (this.runId !== startedRunId) {
          this.resetSummaryItems();
          shouldNotify = false;
          break;
        }
        if (page === 1 && data.total > this.maxRows) {
          this.markExceeded();
          return;
        }
        if (data.result.length === 0) {
          break;
        }
        this.summaryItems.forEach((summaryItem) => {
          summaryItem.addValues(data.result);
        });
        page++;
      }
      if (shouldNotify) {
        this.isLazyDataLoadaded = true;
        this.statusSignal?.set('ready');
        this.summaryItemsSignal?.set(this.summaryItems);
      }
    })();
  }

  private markExceeded(): void {
    this.resetSummaryItems();
    this.isLazyDataLoadaded = false;
    this.summaryItemsSignal?.set([]);
    this.statusSignal?.set('exceeded');
  }

  private async getData(
    currentPage: number,
    pageSize: number
  ): Promise<PagedList<EntityInterface<T>>> {
    return new Promise<PagedList<EntityInterface<T>>>((resolve, reject) => {
      this.selectorsService
        ?.selectDataParsedToEntityInterface$<T>(this.currentRequest!, currentPage, pageSize)
        .pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            reject(err);
            return of();
          })
        )
        .subscribe((data) => {
          resolve(data);
        });
    });
  }
}
